import { formatMonthLabel } from './month-key-utils';
import type { MrrMovementChartDataPoint } from './types';

export type RetentionPoint = {
  monthKey: string;
  month: string;
  nrr: number;
  grr: number;
};

// SaaS retention conventions (per Wall Street Prep, Baremetrics, Drivetrain,
// Corporate Finance Institute, Runway):
//   GRR = (Starting − |Churn| − |Contraction|) / Starting × 100
//   NRR = (Starting + Expansion − |Churn| − |Contraction|) / Starting × 100
// Reactivation is intentionally NOT part of NRR. Standard treatment is to
// classify reactivations either as new business or as offsetting prior churn,
// not as expansion. Reactivation is still tracked as its own bucket in the
// MRR waterfall — it just doesn't roll into NRR.
// Movement amounts are stored signed (churn/contraction are negative),
// so the formulas reduce to plain addition.
export function computeRetentionPoints(
  monthKeys: string[],
  monthlyMovementsByKey: Map<string, MrrMovementChartDataPoint>,
): RetentionPoint[] {
  const points: RetentionPoint[] = [];

  for (const monthKey of monthKeys) {
    const movements = monthlyMovementsByKey.get(monthKey);
    if (!movements || movements.beginningMrr <= 0) continue;

    const { beginningMrr, churn, contraction, expansion } = movements;
    const grr = ((beginningMrr + churn + contraction) / beginningMrr) * 100;
    const nrr = ((beginningMrr + churn + contraction + expansion) / beginningMrr) * 100;

    points.push({
      monthKey,
      month: formatMonthLabel(monthKey),
      nrr: round1(nrr),
      grr: round1(grr),
    });
  }

  return points;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
